'use client';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

interface WeekdayData {
  day: string;
  quantity: number;
  total: number;
  average: number;
}

interface WeekdayAnalysisProps {
  data: WeekdayData[];
}

export function WeekdayAnalysis({ data }: WeekdayAnalysisProps) {
  // Order days of week properly - map English day names from DB to Portuguese
  const dayNames: { [key: string]: string } = {
    'Sunday': 'Domingo',
    'Monday': 'Segunda',
    'Tuesday': 'Terça',
    'Wednesday': 'Quarta',
    'Thursday': 'Quinta',
    'Friday': 'Sexta',
    'Saturday': 'Sábado',
  };
  
  const dayOrder = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
  
  // Convert English day names to Portuguese
  const convertedData = data.map(d => ({
    ...d,
    day: dayNames[d.day] || d.day
  }));
  
  const sortedData = [...convertedData].sort((a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day));

  return (
    <div className="rounded-lg border p-6 mb-8" style={{
      backgroundColor: 'var(--card-bg)',
      borderColor: 'var(--card-border-bright)',
      backdropFilter: 'blur(10px)',
      padding: '1em',
    }}>
      <h2 className="text-xl font-bold mb-6 text-center" style={{ marginBottom: '1em' }}>Vendas por Dia da Semana</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" style={{}}>
        <div className="rounded-lg p-4 border" style={{ borderColor: 'var(--card-border)', padding: '1em' }}>
          <h3 className="text-sm font-semibold text-slate-400 mb-4 text-center">Quantidade por Dia</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sortedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
              <XAxis dataKey="day" stroke="rgba(148, 163, 184, 0.5)" />
              <YAxis stroke="rgba(148, 163, 184, 0.5)" />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'rgba(30, 41, 59, 0.9)',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  borderRadius: '8px',
                }}
              />
              <Bar dataKey="quantity" fill="var(--accent)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-lg p-4 border" style={{ borderColor: 'var(--card-border)', padding: '1em' }}>
          <h3 className="text-sm font-semibold text-slate-400 mb-4 text-center">Total de Vendas por Dia</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sortedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
              <XAxis dataKey="day" stroke="rgba(148, 163, 184, 0.5)" />
              <YAxis stroke="rgba(148, 163, 184, 0.5)" />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'rgba(30, 41, 59, 0.9)',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  borderRadius: '8px',
                }}
                formatter={(value) => `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              />
              <Line 
                type="monotone" 
                dataKey="total" 
                stroke="var(--accent)" 
                strokeWidth={2}
                dot={{ fill: 'var(--accent)', r: 5 }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--card-border)', marginTop: '1em', marginBottom: '1em' }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b" style={{ borderColor: 'var(--card-border)', padding: '0.5em' }}>
              <th className="text-left py-3 px-4 text-slate-400 font-semibold" style={{padding: '0.5em'
              }}>Dia</th>
              <th className="text-center py-3 px-4 text-slate-400 font-semibold" style={{padding: '0.5em'}}>Pedidos</th>
              <th className="text-center py-3 px-4 text-slate-400 font-semibold" style={{padding: '0.5em'}}>Total</th>
              <th className="text-center py-3 px-4 text-slate-400 font-semibold" style={{padding: '0.5em'}}>Médio</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, idx) => (
              <tr key={row.day} className={idx !== sortedData.length - 1 ? "border-b" : ""} style={{ borderColor: 'var(--card-border)', padding: '0.5em' }}>
                <td className="py-3 px-4 font-medium" style={{padding: '0.5em'}}>{row.day}</td>
                <td className="text-center py-3 px-4" style={{padding: '0.5em'}}>{row.quantity}</td>
                <td className="text-center py-3 px-4 text-green-400 font-semibold" style={{padding: '0.5em'}}>R$ {row.total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td className="text-center py-3 px-4" style={{padding: '0.5em'}}>R$ {row.average.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
